// Preact
// eslint-disable-next-line no-unused-vars
import { Fragment, h } from 'preact';
import { useContext } from 'preact/hooks';
import { SearchContext } from '../contexts/Search';
import Pagination from './Pagination';
import User from './User';
import Post from './Post';
import Skeleton from './Skeleton';

const { __ } = wp.i18n;

/**
 * The main component.
 *
 * @return {Object} JSX
 */
const Results = () => {
	const { state } = useContext( SearchContext );
	const { users, posts, loading, foundPosts, sectionName } = state;

	/**
	 * Loop component for loading results.
	 *
	 * @return {Object} JSX
	 */
	const SkeletonLoop = () => (
		<>
			{[...Array( 10 )].map( ( value, index )  => (
				<div className="border-top py-4" key={index}>
					<div className="card card--skeleton">
						<Skeleton className="small mb-3 w-25">&nbsp;</Skeleton>
						<Skeleton className="h3 mt-0 mb-2 w-100" lines={2}>&nbsp;</Skeleton>
						<Skeleton className="base mt-0 mb-2 w-50">&nbsp;</Skeleton>
						<Skeleton className="base mt-0 mb-3 w-25">&nbsp;</Skeleton>
						<Skeleton className="base mt-0 w-100" lines={2}>&nbsp;</Skeleton>
					</div>
				</div>
			) )}
		</>
	);

	/**
	 * Loop component for displaying results, if any.
	 *
	 * @return {Object} JSX
	 */
	const Loop = () => {
		return (
			<div
				id="faceted-search-results"
				role="region"
				aria-label={__( 'Search Results', 'ms-research' )}
				aria-atomic="true"
				aria-busy={loading}
			>
				<div className="faceted-search-results__items">
					<div className="faceted-search-results__items__wrapper">
						{loading ? (
							<SkeletonLoop />
						) : (
							<>
								{users.map( ( user ) => (
									<User key={user.data.ID} user={user} />
								) )}
								{posts.map( ( post ) => (
									<Post key={post.data.ID} post={post} />
								) )}
							</>
						)}
					</div>
				</div>
			</div>
		);
	};

	/**
	 * Loop component for displaying results, if any.
	 *
	 * @returns {object} JSX
	 */
	const ResearchForumLoop = () => {
		const resultsWrapperClassName =
			'faceted-search-results__items__wrapper row row-cols-1 row-cols-sm-2 row-cols-lg-3';

		return (
			<div
				id="faceted-search-results"
				role="region"
				aria-label={__('Search Results', 'ms-research' )}
				aria-atomic="true"
				aria-busy={loading}
			>
				<div className="faceted-search-results__items">
					{loading ? (
						<div className={resultsWrapperClassName}>
							<SkeletonLoop />
						</div>
					) : (
						<div
							className={resultsWrapperClassName}
							dangerouslySetInnerHTML={{
								__html: posts.map((post) => post.markup).join(''),
							}}
						/>
					)}
				</div>
			</div>
		);
	};

	/**
	 * None component for when there are no results.
	 *
	 * @return {Object} JSX
	 */
	const None = () => {
		return (
			<Fragment>
				<h3>{__( 'Nothing Found', 'ms-research' )}</h3>
				{foundPosts && foundPosts.length
					? __(
						'Sorry, your search has matches, but you need to go back to the first page.',
						'ms-research'
					)
					: __(
						'Sorry, but nothing matched your search terms. Please try again with some different keywords.',
						'ms-research'
					)}
			</Fragment>
		);
	};

	// Render component.
	return (
		<div>
			{posts && posts.length ? (
				<Fragment>
					{sectionName === "research-forum" ? <ResearchForumLoop /> : <Loop />}
					<Pagination />
				</Fragment>
			) : (
				<None />
			)}
		</div>
	);
};

export default Results;
